import { useEffect, useState } from "react";
import { FlightTimeline } from "./FlightTimeline";
import { FlightContextMenu } from "src/components/Flights/FlightContextMenu";
import { AircraftContextMenu } from "src/components/AircraftContext/AircraftContextMenu";
import { useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { SpinnerProgress } from "src/components/SpinnerProgress/SpinnerProgress";
import { DialogModalWrapper, EventModalTypes } from "src/components/DialogModal/DialogModalWrapper";
import { styled, alpha, Box } from "@mui/material";
import { ScrollBarWrapper } from "../../SharedGL/ScrollBar/ScrollBarWrapper";
import { textualFlightInfoFormat } from "./TimelineUtils";

interface IFlightTimelineWrapperProps {
  id: string;
  path: string[];
  windowProps: {
    tableDisplay?: boolean;
  };
}

const OCLoadingData = styled("div")(({ theme }) => ({
  width: "200px",
  height: "80px",
  position: "absolute",
  opacity: 1.0,
  bottom: `${60 - theme.custom.statusBar.height}px`,
  right: `${ScrollBarWrapper.scrollBarWidth + 30}px`,
  "& .dialog_spinner_progress": {
    backgroundColor: alpha(theme.palette.common.black, 0.75),
    borderRadius: 3,
    "& svg.oc_spinner": {
      marginLeft: 10,
      width: "54px",
      height: "unset",
    },
    "& .spinner_message": { margin: "0px 10px" },
    "& span.animation": { fontWeight: 300 },
  },
}));

export const FlightTimelineWrapper = (props: IFlightTimelineWrapperProps): JSX.Element => {
  const { id, path, windowProps } = props;
  const flightDataLoading: string[] = useSelector((state: IRootState) => state.timelineFlights.flightDataLoading);
  const flightTimelineCopyTextFormat = useSelector(
    (state: IRootState) => state.UISettings.flightTimelineCopyTextFormat
  );
  const selectedEvents = useSelector((state: IRootState) => state.timelineFlights.selectedEvents);
  const [modalProps, setModalProps] = useState<any>(null);
  const [activeEventModal, setActiveEventModal] = useState<EventModalTypes>(EventModalTypes.None);
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (isActive && (event.ctrlKey || event.metaKey) && event.key === "c") {
        event.preventDefault();
        event.stopPropagation();
        const flightText = textualFlightInfoFormat(selectedEvents, flightTimelineCopyTextFormat);
        navigator.clipboard.writeText(flightText);
      }
    };

    if (isActive) {
      document.addEventListener("keydown", handleKeyDown);
    } else {
      document.removeEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [flightTimelineCopyTextFormat, isActive, selectedEvents]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        flexBasis: 0,
        width: "100%",
        height: windowProps?.tableDisplay ? "100%" : undefined,
        overflow: "hidden",
      }}
    >
      {flightDataLoading.length > 0 && (
        <OCLoadingData>
          <SpinnerProgress loading={true} message={"Loading Flights"} fullscreen={false} />
        </OCLoadingData>
      )}
      <FlightTimeline windowId={id} path={path} setActiveEventModal={setActiveEventModal} setIsActive={setIsActive} />
      <FlightContextMenu
        id={props.id}
        path={props.path}
        setActiveEventModal={setActiveEventModal}
        setModalProps={setModalProps}
      />
      <AircraftContextMenu windowId={props.id} />
      {activeEventModal !== EventModalTypes.None && (
        <DialogModalWrapper
          modalType={activeEventModal}
          setActiveEventModal={setActiveEventModal}
          id={props.id}
          modalProps={modalProps}
        />
      )}
    </div>
  );
};
